import { Redirect, Route, Switch } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  IonApp,
  IonBadge,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  createAnimation,
  setupIonicReact
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { chatbubbleEllipses, gameController, people, settings } from 'ionicons/icons';

import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/palettes/dark.system.css';
import './theme/variables.css';
import './App.css';

import { AppProvider, useSkyChat } from './app/AppContext';
import { BOOT_BACKGROUND_URL } from './utils/bootAssets';

const DeviceTransferProvider = lazy(() => import('./features/DeviceTransferProvider')
  .then((module) => ({ default: module.DeviceTransferProvider })));
const MobileScreenShareProvider = lazy(() => import('./features/MobileScreenShare')
  .then((module) => ({ default: module.MobileScreenShareProvider })));
const VirtualPetProvider = lazy(() => import('./features/VirtualPetProvider')
  .then((module) => ({ default: module.VirtualPetProvider })));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ChatMediaPage = lazy(() => import('./pages/ChatMediaPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatStoragePage = lazy(() => import('./pages/ChatStoragePage'));
const ChatsPage = lazy(() => import('./pages/ChatsPage'));
const ContactInfoPage = lazy(() => import('./pages/ContactInfoPage'));
const DeviceTransferPage = lazy(() => import('./pages/DeviceTransferPage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PetsPage = lazy(() => import('./pages/PetsPage'));
const PersonalizationPage = lazy(() => import('./pages/PersonalizationPage'));
const SharedNotesPage = lazy(() => import('./pages/SharedNotesPage'));

function fastNavigationAnimation(_baseEl: unknown, opts: { enteringEl?: Element; leavingEl?: Element; direction?: string } = {}) {
  const enteringEl = opts.enteringEl;
  const leavingEl = opts.leavingEl;
  const forward = opts.direction !== 'back';
  const root = createAnimation()
    .duration(260)
    .easing('cubic-bezier(0.22, 1, 0.36, 1)');

  if (enteringEl) {
    const entering = createAnimation()
      .addElement(enteringEl)
      .beforeRemoveClass('ion-page-invisible')
      .beforeStyles({ zIndex: forward ? '2' : '1' })
      .fromTo('opacity', forward ? '0.88' : '0.94', '1')
      .fromTo(
        'transform',
        `translate3d(${forward ? '36px' : '-18px'}, 0, 0) scale(${forward ? '0.985' : '1'})`,
        'translate3d(0, 0, 0) scale(1)'
      )
      .afterClearStyles(['z-index', 'opacity', 'transform', 'will-change']);
    root.addAnimation(entering);
  }

  if (leavingEl) {
    const leaving = createAnimation()
      .addElement(leavingEl)
      .beforeStyles({ zIndex: forward ? '1' : '2' })
      .fromTo('opacity', '1', forward ? '0.78' : '0.9')
      .fromTo(
        'transform',
        'translate3d(0, 0, 0) scale(1)',
        `translate3d(${forward ? '-18px' : '42px'}, 0, 0) scale(${forward ? '0.992' : '0.985'})`
      )
      .afterClearStyles(['z-index', 'opacity', 'transform', 'will-change']);
    root.addAnimation(leaving);
  }

  return root;
}

setupIonicReact({
  mode: 'ios',
  animated: true,
  navAnimation: fastNavigationAnimation
});


function useMobileViewportMode() {
  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    let stableLayoutWidth = 0;
    let stableLayoutHeight = 0;

    const update = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const viewport = window.visualViewport;
        const layoutWidth = Math.round(window.innerWidth || root.clientWidth || 0);
        const rawLayoutHeight = Math.round(window.innerHeight || root.clientHeight || 0);
        const width = Math.round(viewport?.width || layoutWidth);
        const height = Math.round(viewport?.height || rawLayoutHeight);
        const keyboardLikeShrink = height > 0 && rawLayoutHeight > 0 && (rawLayoutHeight - height) > 80;
        const widthChanged = stableLayoutWidth > 0 && Math.abs(layoutWidth - stableLayoutWidth) > 40;
        const rawShrinkSameWidth = stableLayoutHeight > 0 && !widthChanged && rawLayoutHeight < (stableLayoutHeight - 80);
        const keyboardOpen = keyboardLikeShrink || rawShrinkSameWidth || root.classList.contains('sky-chat-keyboard-open');

        if (!keyboardOpen || !stableLayoutHeight || widthChanged) {
          stableLayoutWidth = layoutWidth;
          stableLayoutHeight = rawLayoutHeight;
        }

        const layoutHeight = keyboardOpen && stableLayoutHeight ? stableLayoutHeight : rawLayoutHeight;
        const isLandscape = layoutWidth > layoutHeight && layoutHeight <= 520;

        root.style.setProperty('--sky-viewport-width', `${width}px`);
        root.style.setProperty('--sky-viewport-height', `${layoutHeight}px`);
        root.style.setProperty('--sky-visual-viewport-height', `${height}px`);
        root.classList.toggle('sky-mobile-landscape', isLandscape);
        root.classList.toggle('sky-mobile-tight-height', height > 0 && height <= 430);
      });
    };

    update();
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    window.visualViewport?.addEventListener('resize', update, { passive: true });
    window.visualViewport?.addEventListener('scroll', update, { passive: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      root.classList.remove('sky-mobile-landscape', 'sky-mobile-tight-height');
      root.style.removeProperty('--sky-viewport-width');
      root.style.removeProperty('--sky-viewport-height');
      root.style.removeProperty('--sky-visual-viewport-height');
    };
  }, []);
}


function useBlockTabsEdgeBackGesture() {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let blocking = false;

    const isFromInteractiveControl = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return !!target.closest('ion-input, ion-searchbar, ion-textarea, input, textarea, select, [contenteditable="true"]');
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || isFromInteractiveControl(event.target)) {
        blocking = false;
        return;
      }
      startX = touch.clientX;
      startY = touch.clientY;
      blocking = startX <= 34;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!blocking) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);
      if (dx > 6 && dx > dy * 1.2) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const onTouchEnd = () => {
      blocking = false;
    };

    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
    document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', onTouchEnd, { capture: true });
    };
  }, []);
}

function ProtectedTabs() {
  useBlockTabsEdgeBackGesture();
  const { token, ready, t, chat } = useSkyChat();
  if (!ready) return null;
  if (!token) return <Redirect to="/auth" />;
  const unreadCount = Object.values(chat.unreadByPeer).reduce((total, count) => total + Number(count || 0), 0);

  return (
    <IonTabs>
      <IonRouterOutlet animation={fastNavigationAnimation}>
        <Route exact path="/tabs/chats" component={ChatsPage} />
        <Route exact path="/tabs/friends" component={FriendsPage} />
        <Route exact path="/tabs/games" component={GamesPage} />
        <Route exact path="/tabs/settings" component={SettingsPage} />
        <Route exact path="/tabs">
          <Redirect to="/tabs/chats" />
        </Route>
      </IonRouterOutlet>
      <IonTabBar slot="bottom" className="app-tabbar">
        <IonTabButton tab="chats" href="/tabs/chats">
          <span className="tab-icon-wrap">
            <IonIcon icon={chatbubbleEllipses} />
            {unreadCount > 0 ? (
              <IonBadge className="tab-unread-badge" color="danger" aria-label={t('unread')}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </IonBadge>
            ) : null}
          </span>
          <IonLabel>{t('chats')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="friends" href="/tabs/friends">
          <IonIcon icon={people} />
          <IonLabel>{t('friends')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="games" href="/tabs/games">
          <IonIcon icon={gameController} />
          <IonLabel>{t('games')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="settings" href="/tabs/settings">
          <IonIcon icon={settings} />
          <IonLabel>{t('settings')}</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}

function BootScreen({ exiting = false }: { exiting?: boolean }) {
  return (
    <div className={`boot-screen ${exiting ? 'is-exiting' : ''}`}>
      <div
        className="boot-background is-ready"
        style={{ '--boot-background-image': `url("${BOOT_BACKGROUND_URL}")` } as CSSProperties}
        aria-hidden="true"
      />
      <div className="boot-particles" aria-hidden="true">
        {Array.from({ length: 14 }, (_, index) => <i key={index} />)}
      </div>
      <div className="boot-brand">
        <img className="boot-logo-image" src="/icons/chat_192.png" alt="" aria-hidden="true" />
        <strong>SkyChat</strong>
      </div>
    </div>
  );
}

function Routes() {
  const { token, ready, initialUiReady } = useSkyChat();
  const [minimumBootElapsed, setMinimumBootElapsed] = useState(false);
  const [showBoot, setShowBoot] = useState(true);
  const [bootExiting, setBootExiting] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMinimumBootElapsed(true), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  const canEnterApp = ready && initialUiReady && minimumBootElapsed;

  useEffect(() => {
    if (!canEnterApp) {
      setShowBoot(true);
      setBootExiting(false);
      return;
    }
    setBootExiting(true);
    const timer = window.setTimeout(() => setShowBoot(false), 520);
    return () => window.clearTimeout(timer);
  }, [canEnterApp]);

  if (showBoot) return <BootScreen exiting={bootExiting} />;

  const requireAuth = (page: ReactElement) => token ? page : <Redirect to="/auth" />;

  return (
    <Suspense fallback={<BootScreen />}>
      <IonReactRouter>
        <Switch>
          <Route exact path="/auth" component={AuthPage} />
          <Route exact path="/chat/:peerId/info" render={() => requireAuth(<ContactInfoPage />)} />
          <Route exact path="/chat/:peerId/media" render={() => requireAuth(<ChatMediaPage />)} />
          <Route exact path="/chat/:peerId/notes" render={() => requireAuth(<SharedNotesPage />)} />
          <Route exact path="/chat/:peerId/storage" render={() => requireAuth(<ChatStoragePage />)} />
          <Route exact path="/chats/device-transfer" render={() => requireAuth(<DeviceTransferPage />)} />
          <Route exact path="/settings/personalization" render={() => requireAuth(<PersonalizationPage />)} />
          <Route exact path="/settings/pets" render={() => requireAuth(<PetsPage />)} />
          <Route exact path="/chat/:peerId" render={() => requireAuth(<ChatPage />)} />
          <Route path="/tabs" component={ProtectedTabs} />
          <Route exact path="/">
            <Redirect to={token ? '/tabs/chats' : '/auth'} />
          </Route>
        </Switch>
      </IonReactRouter>
    </Suspense>
  );
}

export default function App() {
  useMobileViewportMode();

  return (
    <IonApp>
      <AppProvider>
        <Suspense fallback={<BootScreen />}>
          <DeviceTransferProvider>
            <MobileScreenShareProvider>
              <VirtualPetProvider>
                <Routes />
              </VirtualPetProvider>
            </MobileScreenShareProvider>
          </DeviceTransferProvider>
        </Suspense>
      </AppProvider>
    </IonApp>
  );
}
